import _ from "lodash";

import { WorkspacePermissionObjectType, WorkspacePermissionSubjectType, WorkspaceResourceType } from "common/interfaces";
import { getWorkspacePermissionsToResource } from "js/controllers/WorkspaceController";
import getUserProfile from "js/core/services/userProfiles";
import { AvatarStackProfile } from "js/editor/PresentationLibrary/components/AvatarStack";

export interface FetchCollaboratorProfilesParams {
    workspaceId: string;
    resourceId: string;
    ownerUid: string;
    maxProfileRequests?: number;
}

const collaboratorProfileRequests: Record<string, Promise<AvatarStackProfile[]>> = {};
const MAX_PROFILE_REQUESTS = 4;

function getCollaboratorProfileRequestKey({ workspaceId, resourceId, ownerUid, maxProfileRequests }: FetchCollaboratorProfilesParams) {
    return `${workspaceId}:${resourceId}:${ownerUid}:${maxProfileRequests ?? MAX_PROFILE_REQUESTS}`;
}

/**
 * Fetches collaborator profiles (owner + shared users) for a presentation.
 * Used by both list and grid views to show AvatarStack.
 */
export async function fetchPresentationCollaboratorProfiles({
    workspaceId,
    resourceId,
    ownerUid,
    maxProfileRequests = MAX_PROFILE_REQUESTS,
}: FetchCollaboratorProfilesParams): Promise<AvatarStackProfile[]> {
    const requestKey = getCollaboratorProfileRequestKey({ workspaceId, resourceId, ownerUid, maxProfileRequests });
    if (collaboratorProfileRequests[requestKey]) {
        return collaboratorProfileRequests[requestKey];
    }

    const request = fetchPresentationCollaboratorProfilesUncached({
        workspaceId,
        resourceId,
        ownerUid,
        maxProfileRequests,
    });
    collaboratorProfileRequests[requestKey] = request;

    // Don't cache failed requests, otherwise a transient error (network/5xx) would permanently
    // break this presentation's avatars for the rest of the session. Drop the entry so callers can retry.
    request.catch(() => {
        if (collaboratorProfileRequests[requestKey] === request) {
            delete collaboratorProfileRequests[requestKey];
        }
    });

    return request;
}

async function fetchPresentationCollaboratorProfilesUncached({
    workspaceId,
    resourceId,
    ownerUid,
    maxProfileRequests = MAX_PROFILE_REQUESTS,
}: FetchCollaboratorProfilesParams): Promise<AvatarStackProfile[]> {
    let uids = [ownerUid];
    const permByUid: Record<string, { owner: boolean; write: { object: boolean } }> = {};

    try {
        const permissions = await getWorkspacePermissionsToResource({
            workspaceId,
            resourceId,
            resourceType: WorkspaceResourceType.PRESENTATION,
        });
        const users = permissions.filter(p =>
            p.subjectType === WorkspacePermissionSubjectType.USER &&
            p.objectType === WorkspacePermissionObjectType.WORKSPACE_RESOURCE
        );
        users.forEach(p => permByUid[p.subjectId] = p);
        uids = [ownerUid, ..._.uniq(users.map(p => p.subjectId).filter(uid => uid !== ownerUid))].slice(0, maxProfileRequests);
    } catch {
        // show owner only if permissions fetch fails
    }

    return Promise.all(
        uids.map(uid =>
            getUserProfile(uid).then(({ photoURL, displayName, email }) => ({
                photoURL,
                displayName,
                email,
                isCreator: uid === ownerUid,
                isReadOnly: !permByUid[uid]?.owner && !permByUid[uid]?.write?.object,
            }))
        )
    );
}
